import { useCallback, useEffect, useState } from 'react';
import { HeaderContent, HEADER_TITLE_CLASS } from '../components/shell/app-header';
import { Modal, FormField } from '../components/modal';
import { Button, buttonVariants } from '@/components/ui/button';
import { Link } from '../lib/routing';
import { cn } from '@/lib/utils';
import { Users, UserPlus, Plus, WarningCircle, ArrowClockwise } from '@phosphor-icons/react';
import {
  getMySchoolId,
  listClassGroups,
  listDirectionTeam,
  inviteDelegate,
  revokeGroupAccess,
  type ClassGroup,
  type DirectionDelegate,
} from '../lib/direction';

const listShellClass = "bg-[var(--naia-card)] border border-[var(--naia-line)] rounded-[var(--naia-r-card)] shadow-[var(--naia-shadow-card)] overflow-hidden divide-y divide-[var(--naia-line)]";
const pickBase = "inline-flex items-center gap-[7px] min-h-[38px] rounded-full border-[1.5px] border-[var(--naia-line)] bg-transparent text-[var(--naia-text-secondary)] font-bold text-[13px] px-[14px] py-[6px] cursor-pointer font-[inherit] hover:border-primary transition-colors";

function delegateName(dl: DirectionDelegate): string {
  return [dl.first_name, dl.last_name].filter(Boolean).join(' ').trim() || dl.email || 'Utilisateur';
}

export default function DirectionTeam() {
  const [schoolId, setSchoolId] = useState<string | null>(null);
  const [groups, setGroups] = useState<ClassGroup[]>([]);
  const [delegates, setDelegates] = useState<DirectionDelegate[]>([]);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  const [inviting, setInviting] = useState(false);
  const [email, setEmail] = useState('');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setFailed(false);
    try {
      const sid = await getMySchoolId();
      setSchoolId(sid);
      if (sid) {
        const [ds, gs] = await Promise.all([listDirectionTeam(), listClassGroups()]);
        setDelegates(ds);
        setGroups(gs);
      }
    } catch {
      setDelegates([]);
      setGroups([]);
      setFailed(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  const reload = load;

  const openInvite = () => { setInviting(true); setEmail(''); setSelected(new Set()); setNotice(null); };
  const toggle = (id: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });

  const submit = async () => {
    const mail = email.trim();
    if (!mail || selected.size === 0 || saving) return;
    setSaving(true);
    try {
      const res = await inviteDelegate(mail, [...selected]);
      setNotice(
        res.status === 'invited'
          ? `Invitation envoyée à ${res.email} — un lien pour définir son mot de passe lui a été adressé.`
          : `Accès accordé à ${res.email}.`,
      );
      await reload();
      setInviting(false);
    } catch (e) {
      setNotice(e instanceof Error ? e.message : "Échec de l'invitation.");
    } finally {
      setSaving(false);
    }
  };

  const revoke = async (accessId: string, label: string) => {
    if (!window.confirm(`Retirer l'accès au groupe « ${label} » ?`)) return;
    setNotice(null);
    try {
      await revokeGroupAccess(accessId);
      await reload();
    } catch (e) {
      setNotice(e instanceof Error ? e.message : 'Échec de la révocation.');
    }
  };

  return (
    <>
      <HeaderContent
        width="md"
        showBurger
        eyebrow="Mode direction"
        eyebrowIcon={<Users size={11} weight="bold" />}
        actions={!failed && schoolId && groups.length > 0 && <Button onClick={openInvite}><Plus size={16} /> Inviter un membre</Button>}
      >
        <div className="min-w-0">
          <h1 className={HEADER_TITLE_CLASS}>Équipe de direction</h1>
          <p className="m-0 mt-0.5 text-[13px] sm:text-[13.5px] leading-snug text-muted-foreground">
            Déléguez l'accès direction d'un ou plusieurs groupes de classes à un membre de votre équipe.
          </p>
        </div>
      </HeaderContent>
      <main className="max-w-[880px] mx-auto px-5 pb-16 pt-4">

        {notice && (
          <p className="mb-4 px-[14px] py-[10px] rounded-[10px] bg-primary/10 text-primary text-sm font-semibold">
            {notice}
          </p>
        )}

        {loading ? (
          <p className="text-[var(--naia-text-secondary)] py-4">Chargement…</p>
        ) : failed ? (
          <div className="flex flex-col items-start gap-3 py-4">
            <p className="flex items-start gap-2 m-0 text-[13.5px] text-[var(--naia-text-primary)]">
              <WarningCircle size={18} weight="fill" className="text-destructive shrink-0 mt-px" />
              <span>
                L'équipe de direction n'a pas pu être chargée. Vos groupes et vos délégations sont intacts.
              </span>
            </p>
            <Button variant="outline" size="sm" className="min-h-[44px]" onClick={() => void load()}>
              <ArrowClockwise size={15} weight="bold" />
              Réessayer
            </Button>
          </div>
        ) : !schoolId ? (
          <p className="text-[var(--naia-text-secondary)] py-4">Aucun établissement rattaché.</p>
        ) : groups.length === 0 ? (
          <div className="flex flex-col items-center gap-[10px] text-center px-6 py-10 bg-[var(--naia-card)] border border-dashed border-[var(--naia-line)] rounded-[var(--naia-r-card)]">
            <div className="grid place-items-center w-[52px] h-[52px] rounded-full bg-primary/10 text-primary"><Users size={26} /></div>
            <div className="font-extrabold text-[16px]">Créez d'abord des groupes de classes</div>
            <p className="text-[13.5px] text-[var(--naia-text-secondary)] max-w-[360px] m-0 mb-1 leading-relaxed">
              La délégation se fait par groupe de classes. Créez-en un, puis revenez inviter un membre.
            </p>
            <Link to="/direction/groupes" className={cn(buttonVariants(), 'no-underline')}>
              <Plus size={16} /> Créer un groupe de classes
            </Link>
          </div>
        ) : delegates.length === 0 ? (
          <div className="flex flex-col items-center gap-[10px] text-center px-6 py-10 bg-[var(--naia-card)] border border-dashed border-[var(--naia-line)] rounded-[var(--naia-r-card)]">
            <div className="grid place-items-center w-[52px] h-[52px] rounded-full bg-primary/10 text-primary"><UserPlus size={26} /></div>
            <div className="font-extrabold text-[16px]">Aucun membre délégué</div>
            <p className="text-[13.5px] text-[var(--naia-text-secondary)] max-w-[360px] m-0 mb-1 leading-relaxed">
              Invitez une personne par email et choisissez les groupes qu'elle pourra superviser.
            </p>
            <Button onClick={openInvite}><Plus size={16} /> Inviter un membre</Button>
          </div>
        ) : (
          <div className={listShellClass}>
            {delegates.map((dl) => (
              <div key={dl.user_id} className="flex flex-col gap-[9px] px-4 py-[14px]">
                <div className="flex items-baseline gap-2 flex-wrap">
                  <span className="font-extrabold text-[15px] text-[var(--naia-text-primary)]">{delegateName(dl)}</span>
                  {dl.email && delegateName(dl) !== dl.email && <span className="text-[13px] text-[var(--naia-text-secondary)]">{dl.email}</span>}
                </div>
                {dl.groups.length === 0 ? (
                  <span className="text-[13px] text-[var(--naia-text-secondary)]">Aucun groupe.</span>
                ) : (
                  <div className="flex flex-wrap gap-[6px]">
                    {dl.groups.map((grp) => (
                      <span key={grp.access_id} className="inline-flex items-center gap-1 text-[12.5px] font-bold text-[var(--naia-text-primary)] bg-[var(--naia-card)] border border-[var(--naia-line)] rounded-full py-[3px] pl-[11px] pr-[5px]">
                        {grp.group_name}
                        <button
                          type="button"
                          className="border-none bg-transparent text-[var(--naia-text-secondary)] cursor-pointer text-[16px] leading-none px-[5px] py-0 rounded-full min-h-[26px] hover:text-destructive"
                          title="Retirer l'accès à ce groupe"
                          onClick={() => revoke(grp.access_id, grp.group_name)}
                        >
                          ×
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </main>

      <Modal
        open={inviting}
        title="Inviter un membre de l'équipe"
        onClose={() => setInviting(false)}
        footer={<>
          <Button variant="secondary" onClick={() => setInviting(false)}>Annuler</Button>
          <Button onClick={submit} disabled={saving || !email.trim() || selected.size === 0}>
            {saving ? 'Envoi…' : 'Inviter'}
          </Button>
        </>}
      >
        <FormField label="Email du membre" value={email} onChange={setEmail} placeholder="prenom.nom@etablissement.fr" type="email" autoFocus />
        <div className="text-[11px] font-black tracking-[1px] text-[var(--naia-text-secondary)] uppercase mt-[14px] mb-2">Groupes supervisés</div>
        <div className="flex flex-wrap gap-2 max-h-[46vh] overflow-y-auto p-0.5">
          {groups.map((grp) => {
            const on = selected.has(grp.id);
            return (
              <button
                key={grp.id}
                type="button"
                onClick={() => toggle(grp.id)}
                aria-pressed={on}
                className={cn(pickBase, on && "border-primary bg-primary text-primary-foreground hover:border-primary")}
              >
                {on ? '✓ ' : ''}{grp.name}
              </button>
            );
          })}
        </div>
        <p className="text-[12px] text-[var(--naia-text-secondary)] mt-[10px]">
          {selected.size} groupe{selected.size > 1 ? 's' : ''} sélectionné{selected.size > 1 ? 's' : ''}. Si la personne n'a pas
          encore de compte, il sera créé et un lien « définir le mot de passe » lui sera envoyé.
        </p>
      </Modal>
    </>
  );
}
